import React, { useState, useRef, useEffect } from 'react';

/**
 * 豆包电路助手聊天组件（模拟回复版）
 * 无需调用外部 API，本地模拟聊天回复，直接测试交互效果
 */
const TutorChat: React.FC = () => {
  // 聊天记录：包含用户和助手的消息
  const [messages, setMessages] = useState<{role: 'user' | 'assistant', content: string}[]>([
    { 
      role: 'assistant', 
      content: '你好！我是豆包电路小助手，有任何电路相关的问题都可以问我～比如：串联电路的电流规律、欧姆定律怎么用等。' 
    }
  ]);
  
  // 输入框内容
  const [inputValue, setInputValue] = useState('');
  
  // 加载状态（防止重复发送）
  const [isLoading, setIsLoading] = useState(false);
  
  // 用于自动滚动到最新消息的引用
  const chatEndRef = useRef<HTMLDivElement>(null);

  // 每当聊天记录更新，自动滚动到最底部
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /**
   * 模拟发送消息并返回回复（无需 API 调用）
   */
  const sendMessage = async () => {
    // 空消息或加载中时不发送
    if (!inputValue.trim() || isLoading) return;

    // 1. 定义用户消息（明确类型，避免 TypeScript 报错）
    const userMessage: { role: 'user' | 'assistant', content: string } = { 
      role: 'user', 
      content: inputValue.trim() 
    };
    
    // 2. 添加用户消息到聊天记录，清空输入框
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // 3. 模拟网络延迟（1秒后返回回复）
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 4. 模拟豆包的电路相关回复（可根据问题关键词定制）
    let mockReply = '';
    const userQuestion = inputValue.toLowerCase();

    // 针对不同电路关键词，返回定制化模拟回复
    if (userQuestion.includes('串联') || userQuestion.includes('并联')) {
      mockReply = `串联电路和并联电路的核心区别：
1. 串联：电流只有一条路径，各用电器相互影响（一个断电，全部断电）；
2. 并联：电流有多条路径，各用电器互不影响（比如家里的灯和插座）；
3. 串联电路电压相加，并联电路电压相等。`;
    } else if (userQuestion.includes('欧姆定律') || userQuestion.includes('i=u/r')) {
      mockReply = `欧姆定律是电路的核心定律：
公式：I = U/R（电流 = 电压 ÷ 电阻）
- I（电流）单位：安培（A）
- U（电压）单位：伏特（V）
- R（电阻）单位：欧姆（Ω）
比如：10V 电压加在 5Ω 电阻上，电流就是 2A。`;
    } else if (userQuestion.includes('电流') || userQuestion.includes('电压') || userQuestion.includes('电阻')) {
      mockReply = `电路三大基本量：
1. 电流（I）：电荷的定向移动，像水管里的水流；
2. 电压（U）：推动电流流动的“压力”，像水管里的水压；
3. 电阻（R）：阻碍电流流动的能力，像水管里的障碍物。`;
    } else {
      mockReply = `你问的是：${inputValue.trim()}
我是豆包电路小助手（模拟模式）～
目前支持解答：串联/并联电路、欧姆定律、电流/电压/电阻等基础问题，你可以试试问这些哦！`;
    }

    // 5. 添加模拟回复到聊天记录，结束加载状态
    setMessages(prev => [...prev, { role: 'assistant', content: mockReply }]);
    setIsLoading(false);
  };

  /**
   * 回车发送消息（按住 shift+回车换行）
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // 阻止默认的回车换行
      sendMessage();
    }
  };

  // 渲染聊天界面
  return (
    <div style={{ 
      width: '380px', 
      height: '550px', 
      border: '1px solid #e5e7eb', 
      borderRadius: '12px', 
      display: 'flex', 
      flexDirection: 'column',
      padding: '16px',
      backgroundColor: '#ffffff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      {/* 聊天记录区域 */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        marginBottom: '16px',
        padding: '8px',
        scrollbarWidth: 'thin',
        scrollbarColor: '#d1d5db transparent'
      }}>
        {messages.map((msg, index) => (
          <div 
            key={index} 
            style={{ 
              margin: '12px 0', 
              padding: '10px 16px',
              borderRadius: '12px',
              maxWidth: '85%',
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              backgroundColor: msg.role === 'user' ? '#2563eb' : '#f3f4f6',
              color: msg.role === 'user' ? 'white' : '#111827',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            {msg.content}
          </div>
        ))}

        {/* 加载中的提示 */}
        {isLoading && (
          <div style={{ 
            padding: '10px 16px', 
            color: '#6b7280',
            fontStyle: 'italic'
          }}>
            豆包正在思考中... 🤔
          </div>
        )}

        {/* 用于自动滚动的空元素 */}
        <div ref={chatEndRef} />
      </div>

      {/* 输入框和发送按钮 */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="输入电路相关问题（比如：欧姆定律是什么？）..."
          disabled={isLoading}
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            outline: 'none',
            fontSize: '14px',
            backgroundColor: isLoading ? '#f9fafb' : 'white',
            color: '#111827'
          }}
        />
        <button
          onClick={sendMessage}
          disabled={isLoading || !inputValue.trim()}
          style={{
            padding: '12px 24px',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            opacity: isLoading || !inputValue.trim() ? 0.7 : 1
          }}
        >
          {isLoading ? '发送中...' : '发送'}
        </button>
      </div>
    </div>
  );
};

export default TutorChat;